import { useCallback, useEffect, useState } from "react";
import { copilotApi } from "../api/copilotApi";

export function useCopilotChat({ showToast } = {}) {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]); // [{ role: "user"|"assistant", content: str }]
  const [sending, setSending] = useState(false);

  const [conversations, setConversations] = useState([]); // [{ id, title, provider, model, updated_at }]
  const [loadingConversations, setLoadingConversations] = useState(true);

  // Only meaningful for a NEW conversation -- once a conversation exists,
  // its own provider/model (from `conversations`) is what's actually used,
  // this selection is ignored by the backend.
  const [models, setModels] = useState([]); // [{ provider, model, label }]
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  useEffect(() => {
    copilotApi
      .listModels()
      .then((list) => {
        const rows = Array.isArray(list) ? list : [];
        setModels(rows);
        if (rows.length > 0) {
          setSelectedProvider(rows[0].provider);
          setSelectedModel(rows[0].model);
        }
      })
      .catch(() => {
        // Non-critical -- chat.js's own default (anthropic) still applies
        // server-side if the picker never loads.
      });
  }, []);

  const refreshConversations = useCallback(async () => {
    try {
      const list = await copilotApi.listConversations();
      setConversations(Array.isArray(list) ? list : []);
    } catch {
      // Non-critical — the chat itself still works without the sidebar list.
    } finally {
      setLoadingConversations(false);
    }
  }, []);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  const loadConversation = useCallback(
    async (id) => {
      if (!id || id === conversationId) return;
      try {
        const rows = await copilotApi.getConversation(id);
        setConversationId(id);
        setMessages(
          (Array.isArray(rows) ? rows : []).map((r) => ({ role: r.role, content: r.content }))
        );
      } catch (err) {
        showToast?.(err.message || "Could not load that conversation", "warn");
      }
    },
    [conversationId, showToast]
  );

  const send = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || sending) return;

      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
      setSending(true);
      try {
        const res = await copilotApi.sendMessage(conversationId, trimmed, selectedProvider, selectedModel);
        setConversationId(res.conversation_id);
        setMessages((prev) => [...prev, { role: "assistant", content: res.reply }]);
        refreshConversations();
      } catch (err) {
        showToast?.(err.message || "AI Copilot request failed", "warn");
        // Roll back the optimistic user message so the input can be retried
        // without a dangling unanswered bubble.
        setMessages((prev) => prev.slice(0, -1));
      } finally {
        setSending(false);
      }
    },
    [conversationId, sending, showToast, refreshConversations, selectedProvider, selectedModel]
  );

  const startNewConversation = useCallback(() => {
    setConversationId(null);
    setMessages([]);
  }, []);

  // Setting just the provider also snaps the model to that provider's
  // (currently single) default -- avoids ever sending a provider/model
  // pair that don't belong together.
  const selectProvider = useCallback(
    (providerName) => {
      const found = models.find((m) => m.provider === providerName);
      if (!found) return;
      setSelectedProvider(found.provider);
      setSelectedModel(found.model);
    },
    [models]
  );

  // The ACTIVE conversation's own provider/model (fixed at creation) — not
  // the same as selectedProvider/selectedModel above, which only matters
  // for a conversation that doesn't exist yet.
  const activeConversation = conversations.find((c) => c.id === conversationId) || null;

  return {
    conversationId,
    messages,
    sending,
    send,
    startNewConversation,
    conversations,
    loadingConversations,
    loadConversation,
    models,
    selectedProvider,
    selectedModel,
    selectProvider,
    activeConversation,
  };
}
